import { CustomAccordion } from 'components/CustomAccordion/CustomAccordion'
import { useContractStore } from 'context/ContractStore'
import { differenceInYears } from 'date-fns'
import { getFomattedDate, getFullName, getFullValue } from 'lib/utils'
import React, { memo, useEffect, useState } from 'react'
import { Box, Checkbox, FormControlLabel } from '@mui/material'
import { ACCOUNTANT, HEAD_UNDERWRITER } from '../../lib/constants/roles'
import { useAuthStore } from '../../context/AuthStore'

const clientData = data => {
  if (!data) return []
  return [
    { label: 'ФИО', value: getFullName(data?.firstName, data?.lastName, data?.patronymic) || 'Нет данных' },
    { label: 'ИНН', value: data?.iin || 'Нет данных' },
    {
      label: 'Дата рождения',
      value: data?.birthDate ? getFomattedDate(data?.birthDate) : 'Нет данных'
    },
    {
      label: 'Возраст',
      value: data?.birthDate ? `${differenceInYears(new Date(), new Date(data?.birthDate))} лет` : 'Нет данных'
    },
    { label: 'Мобильный телефон', value: data?.mobilePhone1?.toString()?.toPhone() || 'Нет данных' },
    { label: 'Пол', value: data?.sexName?.name || 'Нет данных' },
    { label: 'Страна гражданства', value: data?.citizenship || 'Нет данных' },
    { label: 'Тип документа', value: data?.documentType?.name || 'Нет данных' },
    { label: 'Резиденство', value: data?.resident ? 'Резидент' : 'Нерезидент' },
    { label: 'Образование', value: data?.education?.name || 'Нет данных' },
    { label: 'Серия документа', value: data?.documentSeries || 'Нет данных' },
    { label: 'Номер документа', value: data?.documentNumber || 'Нет данных' },
    { label: 'Орган выдачи', value: data?.documentIssuer || 'Нет данных' },
    {
      label: 'Дата выдачи',
      value: data?.documentIssueDate ? getFomattedDate(data?.documentIssueDate) : 'Нет данных'
    },
    {
      label: 'Срок действия документа',
      value: data?.documentExpiryDate ? getFomattedDate(data?.documentExpiryDate) : 'Нет данных'
    },
    { label: 'Семейное положение', value: data?.maritalStatus?.name || 'Нет данных' },
    { label: 'Количество иждивенцев', value: data?.numberOfDependents?.toString() || 'Нет данных' },
    { label: 'Тип клиента', value: data?.clientType || 'Нет данных' }
  ]
}

const workInfoData = data => {
  if (!data) return []
  return [
    { label: 'Вид деятельности', value: data?.activityType || 'Нет данных' },
    { label: 'Название организации', value: data?.organizationName || 'Нет данных' },
    { label: 'Должность', value: data?.position || 'Нет данных' },
    { label: 'Позиция должности', value: data?.jobLevel || 'Нет данных' },
    {
      label: 'Общий ежемесячный доход',
      value: data?.monthlyIncome?.toString()?.thousandSeparator() || 'Нет данных'
    },
    { label: 'Стаж работы в организации (мес)', value: data?.employmentDuration?.toString() || 'Нет данных' },
    { label: 'Общий стаж работы', value: data?.totalExperience?.toString() || 'Нет данных' },
    { label: 'Рабочий телефон', value: data?.workPhone?.toString()?.toPhone() || 'Нет данных' },
    {
      label: 'Сумма пенсии',
      value: data?.pensionAmount?.toString()?.thousandSeparator() || 'Нет данных'
    }
  ]
}

const creditInfoData = data => {
  if (!data) return []
  return [
    { label: 'Опыт получения кредита', value: data?.loanExperience || 'Нет данных' },
    {
      label: 'Сумма ежемесячных платежей',
      value: data?.monthlyPaymentAmount?.toString()?.thousandSeparator() || 'Нет данных'
    },
    { label: 'Просрочки по кредиту', value: data?.creditDelinquencies || 'Нет данных' },
    { label: 'Тип документа', value: data?.documentType?.name || 'Нет данных' }
  ]
}

const additionalIncomeData = data => {
  if (!data) return []
  return [
    { label: 'Источник', value: data?.additionalIncomeSource || 'Нет данных' },
    {
      label: 'Ежемесячный доп. доход',
      value: data?.additionalIncomeAmount?.toString()?.thousandSeparator() || 'Нет данных'
    }
  ]
}
const registrationAddressData = data => {
  if (!data) return []
  return [
    { label: 'Страна', value: data?.country?.name || 'Нет данных' },
    { label: 'Область', value: data?.region || 'Нет данных' },
    { label: 'Район', value: data?.district || 'Нет данных' },
    { label: 'Сельский округ', value: data?.ruralArea || 'Нет данных' },
    { label: 'Населённый пункт', value: data?.settlement || 'Нет данных' },
    { label: 'Улица/Микрорайон', value: data?.street || 'Нет данных' },
    { label: 'Дом', value: data?.houseNumber || 'Нет данных' },
    { label: 'Квартира', value: data?.apartment || 'Нет данных' },
    { label: 'Code KATO', value: data?.katoCode || 'Нет данных' },
    {
      label: 'Адрес',
      value: getFullValue(data?.country?.name, data?.region, data?.district, data?.street, data?.houseNumber)
    }
  ]
}

const residenceAddressData = data => {
  if (!data) return []
  return [
    { label: 'Страна', value: data?.country?.name || 'Нет данных' },
    { label: 'Область', value: data?.tempRegion || 'Нет данных' },
    { label: 'Район', value: data?.district || 'Нет данных' },
    { label: 'Сельский округ', value: data?.ruralArea || 'Нет данных' },
    { label: 'Населённый пункт', value: data?.tempSettlement || 'Нет данных' },
    { label: 'Улица/Микрорайон', value: data?.tempStreet || 'Нет данных' },
    { label: 'Дом', value: data?.tempHouseNumber || 'Нет данных' },
    { label: 'Квартира', value: data?.apartment || 'Нет данных' },
    { label: 'Code KATO', value: data?.katoCode || 'Нет данных' },
    {
      label: 'Адрес',
      value: getFullValue(data?.country?.name, data?.region, data?.district, data?.street, data?.houseNumber)
    }
  ]
}

const contactPersonsData = data => {
  if (!data) return []
  return [
    { label: 'ФИО', value: data?.fullName || 'Нет данных' },
    { label: 'Мобильный телефон', value: data?.phone?.toString()?.toPhone() || 'Нет данных' },
    { label: 'Степень родства', value: data?.relationship || 'Нет данных' }
  ]
}

const contactInfoData = data => {
  if (!data) return []
  return [
    { label: 'Мобильный телефон 1', value: data.mobilePhone1?.toString()?.toPhone() || 'Нет данных' },
    { label: 'Мобильный телефон 2', value: data.mobilePhone2?.toString()?.toPhone() || 'Нет данных' },
    { label: 'Email', value: data.email || 'Нет данных' }
  ]
}

const serviceInfoData = data => {
  if (!data) return []
  return [
    { label: 'Менеджер по работе с клиентом', value: data?.activity?.assignToFullNameManager || 'Нет данных' },
    {
      label: 'Дата согласия на обработку персональных данных',
      value: data?.createdDate ? getFomattedDate(data?.createdDate) : 'Нет данных'
    },
    {
      label: 'Дата согласия на передачу данных в кред. бюро',
      value: data?.createdDate ? getFomattedDate(data?.createdDate) : 'Нет данных'
    }
  ]
}

/* TODO const serviceInfoCheckboxData = [
  { label: 'Получать напоминания по SMS или email', checked: false },
  { label: 'В красном списке', checked: false },
  { label: 'В черном списке', checked: false }
]*/

export const ClientTab = memo(({ page, readonly }) => {
  const {
    contractStore: { loanApplicationData, client },
    contractStoreFunc: { setRedBlackCheckBox, getApplicationById }
  } = useContractStore()
  const { authStore } = useAuthStore()

  const getClientData = () => {
    switch (page) {
      case 'client':
        return client

      default:
        return loanApplicationData?.client
    }
  }

  const sourceData = getClientData()
  const [redChecked, setRedChecked] = useState(false)
  const [blackChecked, setBlackChecked] = useState(false)

  useEffect(() => {
    if (loanApplicationData?.client) {
      setRedChecked(!!loanApplicationData.client.inRedList)
      setBlackChecked(!!loanApplicationData.client.inBlackList)
    }
  }, [loanApplicationData?.client?.inRedList, loanApplicationData?.client?.inBlackList])

  return (
    <>
      {authStore?.isUserRole === ACCOUNTANT ? (
        <CustomAccordion title="Клиент" data={clientData(sourceData)} isEdit="client" readonly={readonly} />
      ) : (
        <>
          <CustomAccordion title="Клиент" data={clientData(sourceData)} isEdit="client" readonly={readonly} />
          <CustomAccordion title="Информация о месте работы" data={workInfoData(sourceData)} readonly={readonly} />
          {/* <CustomAccordion
            title="Сведения об имеющихся кредитах"
            data={creditInfoData(sourceData)}
            readonly={readonly}
          /> */}
          <CustomAccordion title="Дополнительный доход" data={additionalIncomeData(sourceData)} readonly={readonly} />
          <CustomAccordion title="Адрес прописки" data={registrationAddressData(sourceData)} readonly={readonly} />
          <CustomAccordion title="Адрес проживания" data={residenceAddressData(sourceData)} readonly={readonly} />
          {(sourceData?.relatives || [])?.map((item, index) => (
            <CustomAccordion
              key={index}
              title={`Контактные лица ${index + 1}`}
              data={contactPersonsData(item)}
              readonly={readonly}
            />
          ))}
          <CustomAccordion title={`Контактные данные`} data={contactInfoData(sourceData)} readonly={readonly} />
          {page !== 'client' && (
            <>
              <CustomAccordion
                title="Служебная информация"
                data={serviceInfoData(loanApplicationData)}
                readonly={readonly}
              />

              {!readonly && authStore?.isUserRole === HEAD_UNDERWRITER && loanApplicationData?.client?.id && (
                <Box sx={{ mt: 2, ml: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={redChecked}
                        disabled={redChecked}
                        onChange={async e => {
                          const value = e.target.checked
                          setRedChecked(value) // обновим UI немедленно
                          await setRedBlackCheckBox({
                            id: loanApplicationData.client.id,
                            inRedList: value,
                            inBlackList: loanApplicationData.client.inBlackList
                          })
                          getApplicationById(loanApplicationData?.id)
                        }}
                      />
                    }
                    label="Красный список"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={blackChecked}
                        disabled={blackChecked}
                        onChange={async e => {
                          const value = e.target.checked
                          setBlackChecked(value) // обновим UI немедленно
                          await setRedBlackCheckBox({
                            id: loanApplicationData.client.id,
                            inRedList: loanApplicationData.client.inRedList,
                            inBlackList: value
                          })
                          getApplicationById(loanApplicationData?.id)
                        }}
                      />
                    }
                    label="Черный список"
                  />
                </Box>
              )}
            </>
          )}
          {/* <CustomAccordion title="Счета клиента" data={[]} /> */}
        </>
      )}
    </>
  )
})
